import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CalendarCheck, CheckCircle2, Sparkles, Lock } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

const api = axios.create({
  baseURL: PUBLIC_API,
  withCredentials: false,
});

// Public booking page — /book/:leadId
// No auth required. Renders lead-personalized greeting + 7-day grid of
// simulated slots, books on click, shows success screen.
export default function BookingPage() {
  const { leadId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lead, setLead] = useState(null);
  const [days, setDays] = useState([]);
  const [booked, setBooked] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/book/${leadId}`);
      setLead(data?.lead || null);
      const grouped = {};
      for (const s of data?.slots || []) {
        if (!grouped[s.day_label]) grouped[s.day_label] = [];
        grouped[s.day_label].push(s);
      }
      setDays(
        Object.entries(grouped).map(([label, slots]) => ({
          day_label: label,
          slots,
        })),
      );
      // Existing booking? Show success screen immediately.
      if (data?.already_booked) {
        setBooked(data.meeting);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't load this booking link.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onBook = async (slot) => {
    if (slot.is_busy) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/book/${leadId}`, {
        start_time: slot.start,
      });
      setBooked(data?.meeting || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't book that slot.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-slate-50"
        data-testid="booking-loading"
      >
        <Loader2 className="h-6 w-6 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen grid place-items-center bg-slate-50 px-6"
        data-testid="booking-error"
      >
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
          <Lock className="h-7 w-7 text-slate-400 mx-auto" />
          <div className="mt-3 text-[14px] text-slate-700 font-semibold">
            This link is no longer active.
          </div>
          <div className="mt-1 text-[12.5px] text-slate-500">{error}</div>
        </div>
      </div>
    );
  }

  if (booked) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-6 py-12"
        data-testid="booking-success"
      >
        <div className="max-w-xl mx-auto bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 sm:p-10 text-center">
          <div className="inline-flex h-14 w-14 rounded-full bg-emerald-100 items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          </div>
          <h1
            className="mt-4 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900"
            data-testid="booking-success-heading"
          >
            Meeting booked successfully
          </h1>
          <p className="mt-2 text-[14px] text-slate-600">
            We've blocked off this time with&nbsp;
            <span className="font-semibold text-slate-900">
              {lead?.name || "you"}
            </span>
            . Calendar invite arrives shortly.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[13px] font-semibold">
            <CalendarCheck className="h-4 w-4" />
            {new Date(booked.start_time).toLocaleString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </div>
          <div className="mt-8 text-[11.5px] text-slate-400">
            Powered by Profit10x · AI Revenue Engine
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 px-6 py-10"
      data-testid="booking-page"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-600 font-bold inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          Profit10x · AI SDR
        </div>
        <h1
          className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900"
          data-testid="booking-heading"
        >
          Book a quick 15-min call
        </h1>
        <p
          className="mt-2 text-[15px] text-slate-600"
          data-testid="booking-greeting"
        >
          Hi <span className="font-semibold text-slate-900">{lead?.name || "there"}</span>
          , let's discuss your use case.
          {lead?.company && (
            <span className="text-slate-500"> Quick chat tailored for {lead.company}.</span>
          )}
        </p>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
          {days.length === 0 && (
            <div className="text-center py-10 text-slate-500 text-[13px]">
              No slots available right now. Reach out and we'll find a time.
            </div>
          )}
          {days.map((d) => (
            <div key={d.day_label} data-testid={`booking-day-${d.day_label}`}>
              <div className="text-[12px] font-bold uppercase tracking-wider text-slate-700 mb-3">
                {d.day_label}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {d.slots.map((s) => (
                  <button
                    type="button"
                    key={s.start}
                    onClick={() => onBook(s)}
                    disabled={s.is_busy || submitting}
                    data-testid={`booking-slot-${s.start}`}
                    data-slot-busy={s.is_busy ? "true" : "false"}
                    className={`text-[13px] font-semibold rounded-lg border px-2.5 py-2 transition-all ${
                      s.is_busy
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through"
                        : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-600 hover:text-white hover:border-indigo-600 hover:shadow-sm"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center text-[11.5px] text-slate-400">
          Booking takes &lt;10 seconds · No login needed · Powered by Profit10x AI
        </div>
      </div>
    </div>
  );
}
